import { Link } from "react-router-dom";
import { formatPrice } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Heart, Clock3 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import API, { getImageUrl } from "@/lib/api";
import { toast } from "sonner";

const normalizePoCloseValue = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return `${text}T23:59`;
  const match = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  return match ? `${match[1]}T${match[2]}` : text;
};

const parsePoCloseAtWib = (value) => {
  const normalized = normalizePoCloseValue(value);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return null;
  const [, y, m, d, hh, mm] = match;
  return new Date(Date.UTC(Number(y), Number(m) - 1, Number(d), Number(hh) - 7, Number(mm), 0, 0));
};

const formatPoCloseWib = (value) => {
  const normalized = normalizePoCloseValue(value);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return value || "";
  const [, y, m, d, hh, mm] = match;
  return `${d}/${m}/${y} ${hh}:${mm} WIB`;
};

const getWibDateParts = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jakarta",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  return {
    year: parts.find((p) => p.type === "year")?.value,
    month: parts.find((p) => p.type === "month")?.value,
    day: parts.find((p) => p.type === "day")?.value,
  };
};

const getPreorderCountdown = (closingDate, locale = "id") => {
  if (!closingDate) return null;

  const target = parsePoCloseAtWib(closingDate);
  if (!target || Number.isNaN(target.getTime())) return null;

  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return null;

  const normalized = normalizePoCloseValue(closingDate);
  const targetDate = normalized.slice(0, 10);
  const nowWib = getWibDateParts(now);
  const todayWib = `${nowWib.year}-${nowWib.month}-${nowWib.day}`;

  if (targetDate === todayWib) {
    const diffMinutes = Math.max(1, Math.ceil(diffMs / (1000 * 60)));
    if (diffMinutes < 60) {
      return { label: locale === "id" ? `${diffMinutes} menit lagi` : `${diffMinutes} min left`, tone: "urgent" };
    }
    const diffHours = Math.ceil(diffMinutes / 60);
    return { label: locale === "id" ? `${diffHours} jam lagi` : `${diffHours} hours left`, tone: "urgent" };
  }

  const todayStartWib = new Date(Date.UTC(Number(nowWib.year), Number(nowWib.month) - 1, Number(nowWib.day), -7, 0, 0, 0));
  const targetStartWib = new Date(Date.UTC(Number(targetDate.slice(0, 4)), Number(targetDate.slice(5, 7)) - 1, Number(targetDate.slice(8, 10)), -7, 0, 0, 0));
  const diffDays = Math.max(1, Math.round((targetStartWib.getTime() - todayStartWib.getTime()) / (1000 * 60 * 60 * 24)));

  if (locale === "id") {
    if (diffDays === 1) return { label: "Tutup besok", tone: "urgent" };
    if (diffDays <= 3) return { label: `${diffDays} hari lagi`, tone: "warning" };
    return { label: `${diffDays} hari lagi`, tone: "calm" };
  }

  if (diffDays === 1) return { label: "Closes tomorrow", tone: "urgent" };
  if (diffDays <= 3) return { label: `${diffDays} days left`, tone: "warning" };
  return { label: `${diffDays} days left`, tone: "calm" };
};

export default function ProductCard({ product, showReseller, wishlistIds = [], onWishlistChange }) {
  const { user } = useAuth();
  const { t, locale } = useLanguage();
  const isWishlisted = wishlistIds.includes(product.id);
  const brandLabel = product.brand_name || product.brand?.name || product.brand_id?.replace("brand_", "") || "";
  const preorderCountdown = product.stock_type === "preorder" && product.is_po_open_calculated === 1
    ? getPreorderCountdown(product.preorder_closing_date, locale)
    : null;

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error(t("product_card.login_required")); return; }
    try {
      const { data } = await API.post("/wishlist/toggle", { product_id: product.id });
      toast.success(data.wishlisted ? t("product_card.added") : t("product_card.removed"));
      onWishlistChange?.();
    } catch { toast.error("Failed"); }
  };

  return (
    <Link to={`/products/${product.id}`} className="group block h-full overflow-hidden rounded-2xl border border-stone-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-md" data-testid={`product-card-${product.id}`}>
      <div className="product-image-hover aspect-[3/4] bg-stone-100 relative overflow-hidden rounded-t-2xl">
          <img
            src={product.images?.[0] ? getImageUrl(product.images[0]) : getImageUrl("naura-numa/assets/frontend/product_fallback.jpg")}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {user && (
            <button
              onClick={handleWishlistToggle}
              className="absolute top-3 right-3 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all opacity-0 group-hover:opacity-100"
              data-testid={`wishlist-btn-${product.id}`}
            >
              <Heart className={`w-4 h-4 ${isWishlisted ? "fill-red-500 text-red-500" : "text-stone-500"}`} />
            </button>
          )}
      </div>
      <div className="space-y-1.5 p-3 sm:p-4">
        <div className="flex items-center gap-2">
          {product.stock_type === "preorder" ? (
            product.is_po_open_calculated !== 1 ? (
              <Badge className="bg-red-50 text-red-700 hover:bg-red-50 text-[10px] uppercase tracking-wider font-bold" data-testid="badge-po-closed">
                {t("admin_products.po_closed") || "Tutup PO"}
              </Badge>
            ) : (
              <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 text-[10px] uppercase tracking-wider font-semibold" data-testid="badge-preorder">
                {t("home.preorder")}
              </Badge>
            )
          ) : (
            <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 text-[10px] uppercase tracking-wider font-semibold" data-testid="badge-ready-stock">
              {t("home.ready_stock")}
            </Badge>
          )}
        </div>
        <p className="font-sans text-xs text-stone-400 uppercase tracking-wider">{brandLabel}</p>
        <h3 className="font-sans text-sm font-medium text-stone-800 group-hover:text-primary transition-colors line-clamp-2" data-testid="product-name">
          {product.name} by {brandLabel}
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {product.pricing && product.pricing.savings > 0 ? (
            <>
              <p className="font-sans text-xs text-stone-400 line-through">{formatPrice(product.pricing.base_price)}</p>
              <p className="font-sans text-sm font-semibold text-stone-900" data-testid="product-price">{formatPrice(product.pricing.final_price)}</p>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${product.pricing.campaign_applied ? "text-purple-700 bg-purple-50" : "text-red-600 bg-red-50"}`}>
                {product.pricing.campaign_applied ? "Campaign" : `-${product.pricing.discount_type === "fixed" ? formatPrice(product.pricing.discount_value) : `${product.pricing.discount_pct}%`}`}
              </span>
            </>
          ) : (
            <p className="font-sans text-sm font-semibold text-stone-900" data-testid="product-price">{formatPrice(product.pricing?.final_price || product.price)}</p>
          )}
        </div>
        {product.stock_type === "preorder" && product.preorder_closing_date && (
          <div className="space-y-1">
            <p className="font-sans text-xs text-amber-600" data-testid="preorder-closing">
              {t("product_card.closes")}: {formatPoCloseWib(product.preorder_closing_date)}
            </p>
            {preorderCountdown && (
              <div
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-semibold ${
                  preorderCountdown.tone === "urgent"
                    ? "border-red-200 bg-red-50 text-red-700"
                    : preorderCountdown.tone === "warning"
                      ? "border-amber-200 bg-amber-50 text-amber-700"
                      : "border-stone-200 bg-stone-50 text-stone-600"
                }`}
              >
                <Clock3 className="w-3 h-3" />
                <span>{preorderCountdown.label}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
